import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  Coffee,
  Clock,
  LogIn,
  LogOut,
  ChevronDown,
  Utensils,
  User,
  Download,
  FileText,
  Fingerprint,
  Search,
  CircleUser,
  Settings,
  LifeBuoy,
  X,
  Menu,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSidebarMenu, SidebarMenuItem } from "../../hooks/useSidebarMenu";
import {
  useGetDocumentsQuery,
  useDownloadDocumentMutation,
} from "../../store/apis/uploads.api";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { useAuth } from "../../store/hooks/useAuth";
import { ConfirmationModal, SimpleTooltip } from "../common";
import { RoleTypeEnum } from "../../utils/constants";
import {
  useGetTodayStatusQuery,
  useCheckInMutation,
  useCheckOutMutation,
  useStartBreakMutation,
  useEndBreakMutation,
} from "../../store/apis/attendance.api";
import type { ILocation } from "../../store/apis/attendance.api";
import { AttendanceUtils } from "../../store/apis/utils/attendanceUtils";
import { useSignedUrl } from "../../store/hooks/useSignedUrl";
import { toast } from "react-hot-toast";
import { OFFICE_CONFIG } from "../../utils/config";
import { useLayout } from "../../contexts/LayoutContext";
import { getAvatarGradient, getInitials } from "../../utils/avatarUtils";

export interface QuickLinkDocument {
  _id?: string;
  id: string;
  path: string;
  name: string;
  filename: string;
  title?: string;
}

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const profilePicSrc = useSignedUrl(user?.profilePic);
  const attendanceEnabled = useHasPermission(PERMISSIONS.MY_ATTENDANCE_MANAGE);
  const { toggleSidebar, isSidebarOpen, breakpoint } = useLayout();

  const attendanceState = useAppSelector((state) => state.attendance);

  const { isLoading: isStatusLoading } = useGetTodayStatusQuery(undefined, {
    skip: !user || !attendanceEnabled,
    refetchOnMountOrArgChange: true,
  });

  const [checkIn, { isLoading: isCheckInLoading }] = useCheckInMutation();
  const [checkOut, { isLoading: isCheckOutLoading }] = useCheckOutMutation();
  const [startBreak, { isLoading: isStartBreakLoading }] =
    useStartBreakMutation();
  const [endBreak, { isLoading: isEndBreakLoading }] = useEndBreakMutation();
  const {
    data: documentsResponse,
    isLoading: isLoadingDocuments,
    refetch: refetchDocuments,
  } = useGetDocumentsQuery(
    { prefix: "Documents" },
    { skip: !user, refetchOnMountOrArgChange: false },
  );
  const [downloadDocument] = useDownloadDocumentMutation();

  const loading =
    isStatusLoading ||
    isCheckInLoading ||
    isCheckOutLoading ||
    isStartBreakLoading ||
    isEndBreakLoading;

  const todayStatus = React.useMemo(() => {
    if (!attendanceState) return null;
    const s = attendanceState.status?.toLowerCase() || "not_checked_in";

    return {
      checkInTime: attendanceState.checkInTime,
      checkOutTime:
        s === "checked_out" || s === "completed" ? "completed" : null,
      canStartBreak: s === "present" || s === "working",
      canEndBreak: s === "on_break" || s === "break",
      activeBreak:
        s === "on_break" || s === "break"
          ? { startTime: attendanceState.breakStartTime }
          : null,
      status: s,
    };
  }, [attendanceState]);

  const [showBreakTypes, setShowBreakTypes] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingDownloads, setLoadingDownloads] = useState(false);
  const [checkOutWarning, setCheckOutWarning] = useState(false);
  const [logoutWarning, setLogoutWarning] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  const { menuItems } = useSidebarMenu();

  const flattenedMenu = useMemo(() => {
    const result: { label: string; path: string; icon: unknown }[] = [];
    for (const item of menuItems) {
      if (item.type === "group" && item.items) {
        for (const sub of item.items) {
          if (sub.path && !sub.disabled) {
            result.push({ label: sub.label, path: sub.path, icon: sub.icon });
          }
        }
      } else if (item.path && !item.disabled) {
        result.push({ label: item.label, path: item.path, icon: item.icon });
      }
    }
    return result;
  }, [menuItems]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return flattenedMenu
      .filter((item) => item.label.toLowerCase().includes(q))
      .slice(0, 10);
  }, [searchQuery, flattenedMenu]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchResults]);

  const handleSearchSelect = useCallback(
    (path: string) => {
      setSearchQuery("");
      setSearchFocused(false);
      setHighlightedIndex(-1);
      setMobileSearchOpen(false);
      searchInputRef.current?.blur();
      navigate(path);
    },
    [navigate],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (breakpoint === "mobile" || breakpoint === "tablet") {
          setMobileSearchOpen(true);
          setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
        } else {
          searchInputRef.current?.focus();
          setSearchFocused(true);
        }
      }
      if (e.key === "Escape") {
        setSearchFocused(false);
        setSearchQuery("");
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [breakpoint]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    if (searchFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchFocused]);

  const downloadOptions: QuickLinkDocument[] = React.useMemo(() => {
    const filePaths: string[] = documentsResponse?.data ?? [];
    return filePaths
      .filter(
        (path): path is string =>
          typeof path === "string" && !path.endsWith("/"),
      )
      .map((path) => ({
        id: path,
        path,
        name: path.split("/").pop() || path,
        filename: path.split("/").pop() || path,
      }));
  }, [documentsResponse?.data]);

  useEffect(() => {
    if (todayStatus && window.ReactNativeWebView) {
      const isActive = !!todayStatus.checkInTime && !todayStatus.checkOutTime;
      if (isActive) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "START_MOBILE_SERVICES",
            payload: {
              status: "checked_in",
              checkInTime: todayStatus.checkInTime,
              source: "header_sync",
            },
          }),
        );
      }
    }
  }, [todayStatus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".break-dropdown")) setShowBreakTypes(false);
      if (!target.closest(".download-dropdown")) setShowDownloadDropdown(false);
      if (!target.closest(".user-dropdown")) setShowUserDropdown(false);
    };

    if (showBreakTypes || showDownloadDropdown || showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showBreakTypes, showDownloadDropdown, showUserDropdown]);

  const handleDownloadDropdownToggle = () => {
    const next = !showDownloadDropdown;
    setShowDownloadDropdown(next);
    if (next && user) refetchDocuments();
  };

  const getCurrentLocation = (): Promise<ILocation> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true },
      );
    });
  };

  const handleDownload = async (item: QuickLinkDocument) => {
    try {
      setLoadingDownloads(true);
      const fileName = item.path || item.id;
      const blob = await downloadDocument(fileName).unwrap();

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          item.filename ||
          item.name ||
          item.title ||
          `download-${item.id || item._id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setLoadingDownloads(false);
      setShowDownloadDropdown(false);
    }
  };

  const [handleCheckInLoading, setHandleCheckInLoading] = useState(false);
  const [handleCheckOutLoading, setHandleCheckOutLoading] = useState(false);

  const locationConfig = user?.locationConfig;
  const enforceOfficeCheckin = locationConfig?.enforceOfficeCheckin ?? false;

  const handleCheckInAction = async () => {
    setHandleCheckInLoading(true);
    try {
      let location: ILocation = { latitude: 0, longitude: 0, accuracy: 0 };
      try {
        location = await getCurrentLocation();
      } catch (error) {
        if (enforceOfficeCheckin) {
          toast.error("Error fetching location");
          console.error("Error fetching location:", error);
          return;
        }
      }

      if (enforceOfficeCheckin) {
        const officeLat = locationConfig!.latitude!;
        const officeLng = locationConfig!.longitude!;
        const allowedRadius = locationConfig!.allowedRadiusMeters ?? OFFICE_CONFIG.allowedRadiusMeters;

        const distance = AttendanceUtils.calculateDistance(
          location.latitude,
          location.longitude,
          officeLat,
          officeLng,
        );

        const isValid = AttendanceUtils.isLocationValid(distance, allowedRadius);

        if (!isValid) {
          toast.error(
            `You are outside office premises. Distance: ${Math.round(distance)}m`,
          );
          return;
        }
      }

      const { date, time } = AttendanceUtils.getFormattedDateTime();

      await checkIn({
        date: date,
        checkInTime: time,
        checkInLocation: location,
      }).unwrap();

      if (window.ReactNativeWebView) {
        const packet = JSON.stringify({
          type: "START_MOBILE_SERVICES",
          payload: { status: "checked_in", time: new Date().toISOString() },
        });
        window.ReactNativeWebView.postMessage(packet);
      }
    } catch (error) {
      console.error("Error checking in:", error);
      toast.error("Error checking in");
    } finally {
      setHandleCheckInLoading(false);
    }
  };

  const handleCheckOutAction = async () => {
    setHandleCheckOutLoading(true);
    try {
      let location: ILocation = { latitude: 0, longitude: 0, accuracy: 0 };
      try {
        location = await getCurrentLocation();
      } catch (error) {
        console.warn("Could not fetch location during checkout, proceeding anyway:", error);
      }

      const { date, time } = AttendanceUtils.getFormattedDateTime();
      await checkOut({
        date: date,
        checkOutTime: time,
        checkOutLocation: location,
      }).unwrap();

      if (window.ReactNativeWebView) {
        const packet = JSON.stringify({
          type: "STOP_MOBILE_SERVICES",
          payload: { status: "checked_out", time: new Date().toISOString() },
        });
        window.ReactNativeWebView.postMessage(packet);
      }
    } catch (error) {
      console.error("Error checking out:", error);
      toast.error("Error checking out");
    } finally {
      setCheckOutWarning(false);
      setHandleCheckOutLoading(false);
    }
  };

  const handleStartBreak = async (breakType: string = "lunch") => {
    try {
      const type = breakType as "lunch" | "tea" | "personal";
      const { date, time } = AttendanceUtils.getFormattedDateTime();

      await startBreak({
        date: date,
        startTime: time,
        type: type,
      }).unwrap();

      setShowBreakTypes(false);
    } catch (error) {
      console.error("Error starting break:", error);
    }
  };

  const handleEndBreak = async () => {
    try {
      const { date, time } = AttendanceUtils.getFormattedDateTime();
      await endBreak({
        date: date,
        endTime: time,
      }).unwrap();
    } catch (error) {
      console.error("Error ending break:", error);
    }
  };

  const getStatusConfig = () => {
    const status = todayStatus?.status || "not_checked_in";
    switch (status) {
      case "not_checked_in":
        return { dot: "bg-[var(--color-text-tertiary)]", text: "Not checked in", chipBg: "bg-[var(--color-neutral-chip-bg)] text-[var(--color-neutral-chip-text)]" };
      case "present":
      case "working": {
        const timeStr = todayStatus?.checkInTime
          ? `Present ${new Date(todayStatus.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : "Working";
        return { dot: "bg-[var(--color-success)]", text: timeStr, chipBg: "bg-[var(--color-success-light)] text-[var(--color-success-dark)]" };
      }
      case "on_break":
      case "break":
        return { dot: "bg-[var(--color-warning)]", text: "On break", chipBg: "bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]" };
      case "checked_out":
      case "completed":
        return { dot: "bg-[var(--color-error)]", text: "Checked out", chipBg: "bg-[var(--color-error-light)] text-[var(--color-error-dark)]" };
      default:
        return { dot: "bg-[var(--color-text-tertiary)]", text: "Not checked in", chipBg: "bg-[var(--color-neutral-chip-bg)] text-[var(--color-neutral-chip-text)]" };
    }
  };


  const statusCfg = getStatusConfig();

  const isMobile = breakpoint === "mobile";

  return (
    <>
      <header className="h-16 flex-none flex items-center gap-1 xs:gap-2 sm:gap-3 lg:gap-4 px-2 xs:px-3 sm:px-4 lg:px-6 bg-white/[0.82] backdrop-blur-[12px] border-b border-[var(--color-layout-header-border)] sticky top-0 z-30">
        {/* Mobile menu toggle */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden flex items-center justify-center w-9 h-9 rounded-[10px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] hover:text-primary-700 transition-all duration-200"
        >
          {isSidebarOpen ? (
            <X className="w-[18px] h-[18px]" />
          ) : (
            <Menu className="w-[18px] h-[18px]" />
          )}
        </button>

        {/* Mobile search trigger */}
        <button
          onClick={() => {
            setMobileSearchOpen(true);
            setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
          }}
          className="lg:hidden flex items-center justify-center w-9 h-9 rounded-[10px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] hover:text-primary-700 transition-all duration-200"
        >
          <Search className="w-[16px] h-[16px]" />
        </button>

        {/* Desktop Search */}
        <div ref={searchRef} className="relative hidden lg:block w-[220px] xl:w-[300px] max-w-[30vw]">
          <div
            className={`flex items-center gap-[9px] h-10 px-[14px] rounded-[11px] transition-all duration-150 ${searchFocused
              ? "bg-white border border-[var(--color-layout-header-search-focus-border)] shadow-[0_0_0_3px_var(--color-layout-header-search-focus-ring)]"
              : "bg-[var(--color-bg-muted)] border border-transparent"
              }`}
          >
            <Search className="w-[13.5px] h-[13.5px] text-[var(--color-text-tertiary)] flex-shrink-0" />
            <input
              ref={searchInputRef}
              placeholder="Search modules, employees…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev < searchResults.length - 1 ? prev + 1 : 0,
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : searchResults.length - 1,
                  );
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
                  if (searchResults[idx]) {
                    handleSearchSelect(searchResults[idx].path);
                  }
                }
              }}
              className="flex-1 bg-transparent border-none outline-none font-inherit text-[13.5px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
            />
            <span className="text-[10.5px] font-semibold text-[var(--color-text-tertiary)] bg-white border border-[var(--color-layout-dropdown-badge-border)] rounded-[6px] px-[6px] py-[2px] flex-shrink-0">
              ⌘K
            </span>
          </div>

          {searchFocused && searchQuery.trim() && (
            <div className="absolute top-[46px] left-0 z-50 w-full min-w-[340px] bg-white border border-[var(--color-layout-header-border)] rounded-[14px] shadow-[var(--shadow-lg)] overflow-hidden ck-fade">
              {searchResults.length > 0 ? (
                <>
                  <div className="px-[10px] pt-[6px] pb-[4px] text-[11px] font-bold text-[var(--color-text-tertiary)] tracking-[0.05em] uppercase">
                    Jump to
                  </div>
                  {searchResults.map((r, i) => (
                    <button
                      key={r.path}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSearchSelect(r.path);
                      }}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      className={`w-full flex items-center gap-[11px] px-3 py-[10px] text-left transition-colors ${i === highlightedIndex
                        ? "bg-[var(--color-bg-muted)]"
                        : "hover:bg-[var(--color-bg-muted)]"
                        }`}
                    >
                      <span className="w-[32px] h-[32px] rounded-[9px] flex-none grid place-items-center bg-[var(--color-bg-muted)]">
                        {React.createElement(r.icon as React.ComponentType<any>, {
                          className: "w-[16px] h-[16px] text-primary-600",
                        })}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-[var(--color-text-primary)]">
                          {r.label}
                        </div>
                        <div className="text-[11px] text-[var(--color-text-tertiary)]">
                          {r.path}
                        </div>
                      </div>
                      <ArrowRight className="w-[14px] h-[14px] text-[var(--color-layout-header-icon-secondary)] flex-shrink-0" />
                    </button>
                  ))}
                  <div className="px-3 py-[10px] border-t border-[var(--color-layout-dropdown-separator)] text-[12px] text-[var(--color-text-tertiary)]">
                    Press <kbd className="bg-[var(--color-bg-muted)] rounded-[5px] px-[6px] py-[1px] text-[11px]">Enter</kbd> to navigate
                  </div>
                </>
              ) : (
                <div className="px-4 py-6 text-[13px] text-[var(--color-text-tertiary)] text-center font-medium">
                  No modules found for "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Attendance widget */}
        {attendanceEnabled && (
          <div className="flex items-center gap-[10px]">
            <span
              className={`flex items-center gap-[5px] xs:gap-[6px] h-8 px-2 xs:px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap flex-none ${statusCfg.chipBg}`}
            >
              <span className={`w-[7px] h-[7px] rounded-full flex-none ${statusCfg.dot}`} />
              <span className="hidden sm:inline">{statusCfg.text}</span>
            </span>

            {todayStatus?.checkInTime && !todayStatus?.checkOutTime ? (
              <>
                {todayStatus.canStartBreak && (
                  <div className="relative break-dropdown block">
                    <button
                      onClick={() => setShowBreakTypes(!showBreakTypes)}
                      disabled={loading}
                      className="h-9 flex items-center gap-[6px] px-3 rounded-[10px] border border-[var(--color-layout-header-border)] bg-transparent text-[var(--color-text-secondary)] text-[12.5px] font-semibold cursor-pointer transition-all duration-150 hover:bg-[var(--color-bg-muted)] hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Coffee className="w-[15px] h-[15px]" />
                      <span className="hidden md:inline">Break</span>
                      <ChevronDown className="w-[14px] h-[14px]" />
                    </button>

                    {showBreakTypes && (
                      <div className="absolute top-full left-0 mt-2 w-44 bg-white border border-[var(--color-layout-header-border)] rounded-[14px] shadow-lg z-50 py-1">
                        <button
                          onClick={() => handleStartBreak("lunch")}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] rounded-[10px] transition-colors"
                        >
                          <Utensils className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                          Lunch
                        </button>
                        <button
                          onClick={() => handleStartBreak("tea")}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] rounded-[10px] transition-colors"
                        >
                          <Coffee className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                          Tea
                        </button>
                        <button
                          onClick={() => handleStartBreak("personal")}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] rounded-[10px] transition-colors"
                        >
                          <User className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                          Personal
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {todayStatus.canEndBreak && (
                  <button
                    onClick={handleEndBreak}
                    disabled={loading}
                    className="h-9 flex items-center gap-[6px] px-3 rounded-[10px] border border-[var(--color-layout-header-border)] bg-transparent text-[var(--color-text-secondary)] text-[12.5px] font-semibold cursor-pointer transition-all duration-150 hover:bg-[var(--color-bg-muted)] hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex"
                  >
                    <Clock className="w-[15px] h-[15px]" />
                    <span className="hidden md:inline">End Break</span>
                  </button>
                )}

                <button
                  onClick={() => setCheckOutWarning(true)}
                  disabled={loading || handleCheckOutLoading}
                  className="h-9 lg:h-10 flex items-center gap-1 xs:gap-2 px-2 xs:px-3 lg:px-4 rounded-[10px] lg:rounded-[11px] border-none bg-[var(--color-error-light)] text-[var(--color-error-dark)] text-[12.5px] lg:text-[13px] font-bold cursor-pointer transition-all duration-150 hover:bg-[var(--color-error-hover)] hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogOut className="w-[15px] lg:w-[16px] h-[15px] lg:h-[16px]" />
                  <span className="hidden md:inline">Check out</span>
                </button>
              </>
            ) : !todayStatus?.checkInTime ? (
              <button
                onClick={handleCheckInAction}
                disabled={loading || handleCheckInLoading}
                className="h-9 lg:h-10 flex items-center gap-1 xs:gap-2 px-2 xs:px-3 lg:px-[18px] rounded-[10px] lg:rounded-[11px] border-none bg-gradient-to-br from-primary-700 to-primary-600 text-white text-[12.5px] lg:text-[13px] font-bold cursor-pointer shadow-[0_6px_16px_-6px_var(--color-layout-accent-ring)] transition-all duration-150 hover:-translate-y-[1px] hover:shadow-[0_10px_22px_-8px_var(--color-layout-accent-ring)] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Fingerprint className="w-[15px] lg:w-[16px] h-[15px] lg:h-[16px]" />
                <span className="hidden md:inline">Check in</span>
              </button>
            ) : null}
          </div>
        )}

        <div className="w-[1px] h-[26px] bg-[var(--color-layout-header-border)] flex-none" />

        {/* Quick Documents */}
        <div className="relative download-dropdown block">
          <SimpleTooltip label="Documents" side="bottom">
            <button
              onClick={handleDownloadDropdownToggle}
              className="w-9 h-9 flex items-center justify-center rounded-[10px] text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] transition-all duration-200"
            >
              <FileText className="w-[19px] h-[19px]" />
            </button>
          </SimpleTooltip>

          {showDownloadDropdown && (
            <div className="absolute top-[46px] left-1/2 -translate-x-1/2 md:left-auto md:right-0 md:translate-x-0 z-40 w-[268px] bg-white border border-[var(--color-layout-header-border)] rounded-[14px] shadow-[var(--shadow-lg)] p-2">
              <div className="px-[10px] py-[6px] text-[11px] font-bold text-[var(--color-text-tertiary)] tracking-[0.05em] uppercase">
                Quick documents
              </div>
              {isLoadingDocuments || loadingDownloads ? (
                <div className="flex items-center justify-center py-4 text-[13px] text-[var(--color-text-tertiary)] font-medium">
                  Loading...
                </div>
              ) : downloadOptions.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-[var(--color-text-tertiary)] text-center font-medium">
                  No documents available
                </div>
              ) : (
                downloadOptions.map((item) => (
                  <button
                    key={item.id || item._id || item.path}
                    onClick={() => handleDownload(item)}
                    className="w-full flex items-center gap-[11px] px-[10px] py-[9px] rounded-[10px] text-left transition-colors hover:bg-[var(--color-bg-muted)]"
                  >
                    <span className="w-[34px] h-[34px] rounded-[9px] flex-none grid place-items-center bg-[var(--color-bg-muted)]">
                      <Download className="w-[17px] h-[17px] text-[var(--color-text-secondary)]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[var(--color-text-primary)] truncate">
                        {item.name.split(".")[0] || item.title || item.filename || "Download"}
                      </div>
                      <div className="text-[11px] text-[var(--color-text-tertiary)] truncate">
                        {item.name || ""}
                      </div>
                    </div>
                    <Download className="w-[15px] h-[15px] text-[var(--color-layout-header-icon-secondary)] flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="w-[1px] h-[26px] bg-[var(--color-layout-header-border)] flex-none mx-[2px]" />

        {/* Profile pill + dropdown */}
        <div className="relative user-dropdown">
          <div
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex items-center gap-[6px] xs:gap-[9px] h-10 xs:h-11 px-1 xs:px-[6px] lg:pr-[6px] lg:pl-[7px] border border-[var(--color-layout-header-border)] rounded-[10px] xs:rounded-[13px] bg-white cursor-pointer transition-all duration-150 hover:border-[var(--color-layout-header-pill-border-hover)] hover:shadow-[0_4px_12px_-6px_var(--color-layout-header-pill-shadow)]"
          >
            <div className="hidden xl:block text-right leading-[1.1]">
              <div className="text-[12.5px] font-bold text-[var(--color-text-primary)] whitespace-nowrap">
                {user?.fullName || "User"}
              </div>
              <div className="text-[10.5px] font-semibold text-[var(--color-text-tertiary)] whitespace-nowrap">
                {user?.role || ""}
              </div>
            </div>
            <div
              className="w-[32px] h-[32px] rounded-[9px] flex-none grid place-items-center text-white font-bold text-[12px] shadow-xs"
              style={{ background: getAvatarGradient(user?.fullName || "User") }}
            >
              {user ? (
                user.profilePic && !imgError ? (
                  <img
                    src={profilePicSrc}
                    alt=""
                    className="w-full h-full rounded-[9px] object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  getInitials(user.fullName)
                )
              ) : "U"}
            </div>
            <ChevronDown className="w-[15px] h-[15px] text-[var(--color-text-tertiary)] hidden xl:block" />
          </div>

          {showUserDropdown && (
            <div className="absolute top-[50px] right-0 z-40 w-[240px] bg-white border border-[var(--color-layout-header-border)] rounded-[14px] shadow-[var(--shadow-lg)] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-4 bg-[var(--color-bg-hover)] border-b border-[var(--color-layout-dropdown-separator)]">
                <div
                  className="w-[42px] h-[42px] rounded-[12px] flex-none grid place-items-center text-white font-bold text-[14px] shadow-xs"
                  style={{ background: getAvatarGradient(user?.fullName || "User") }}
                >
                  {user ? (
                    user.profilePic && !imgError ? (
                      <img
                        src={profilePicSrc}
                        alt=""
                        className="w-full h-full rounded-[12px] object-cover"
                      />
                    ) : (
                      getInitials(user.fullName)
                    )
                  ) : "U"}
                </div>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-bold text-[var(--color-text-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                    {user?.fullName || "User"}
                  </div>
                  <div className="text-[11.5px] text-[var(--color-text-tertiary)] whitespace-nowrap overflow-hidden text-ellipsis">
                    {user?.role || ""}
                  </div>
                </div>
              </div>

              <div className="p-[6px]">
                <button
                  onClick={() => {
                    setShowUserDropdown(false);
                    navigate("/my-profile");
                  }}
                  className="w-full flex items-center gap-[11px] px-[11px] py-[10px] rounded-[10px] text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] transition-colors"
                >
                  <CircleUser className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                  My Profile
                </button>
                <button
                  onClick={() => {
                    setShowUserDropdown(false);
                    navigate("/change-password");
                  }}
                  className="w-full flex items-center gap-[11px] px-[11px] py-[10px] rounded-[10px] text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] transition-colors"
                >
                  <Settings className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    setShowUserDropdown(false);
                    navigate("/help-center");
                  }}
                  className="w-full flex items-center gap-[11px] px-[11px] py-[10px] rounded-[10px] text-[13px] font-semibold text-[var(--color-layout-header-icon)] hover:bg-[var(--color-bg-muted)] transition-colors"
                >
                  <LifeBuoy className="w-[16px] h-[16px] text-[var(--color-text-secondary)]" />
                  Help Center
                </button>

                <div className="h-[1px] bg-[var(--color-layout-dropdown-separator)] mx-[6px] my-[5px]" />

                <button
                  onClick={() => {
                    setLogoutWarning(true);
                    setShowUserDropdown(false);
                  }}
                  className="w-full flex items-center gap-[11px] px-[11px] py-[10px] rounded-[10px] text-[13px] font-bold text-[var(--color-error)] hover:bg-[var(--color-error-light)] transition-colors"
                >
                  <LogOut className="w-[16px] h-[16px]" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile search overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 bg-white lg:hidden">
          <div className="flex items-center gap-3 px-4 h-16 border-b border-[var(--color-layout-header-border)]">
            <button
              onClick={() => {
                setMobileSearchOpen(false);
                setSearchQuery("");
              }}
              className="flex items-center justify-center w-9 h-9 rounded-[10px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] transition-all duration-200"
            >
              <ArrowRight className="w-[18px] h-[18px] rotate-180" />
            </button>
            <div className="flex-1 flex items-center gap-[9px] h-10 px-[14px] rounded-[11px] bg-[var(--color-bg-muted)]">
              <Search className="w-[20px] h-[20px] text-[var(--color-text-tertiary)] flex-shrink-0" />
              <input
                ref={mobileSearchInputRef}
                placeholder="Search modules, employees…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none font-inherit text-[15px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
              />
            </div>
          </div>
          <div className="p-4">
            {searchResults.length > 0 ? (
              <div className="space-y-1">
                {searchResults.map((r) => (
                  <button
                    key={r.path}
                    onClick={() => handleSearchSelect(r.path)}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-[11px] text-left hover:bg-[var(--color-bg-muted)] transition-colors"
                  >
                    <span className="w-[36px] h-[36px] rounded-[9px] flex-none grid place-items-center bg-[var(--color-bg-muted)]">
                      {React.createElement(r.icon as React.ComponentType<any>, {
                        className: "w-[18px] h-[18px] text-primary-600",
                      })}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-bold text-[var(--color-text-primary)]">
                        {r.label}
                      </div>
                      <div className="text-[12px] text-[var(--color-text-tertiary)]">
                        {r.path}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div className="py-8 text-center text-[14px] text-[var(--color-text-tertiary)] font-medium">
                No modules found for "{searchQuery}"
              </div>
            ) : null}
          </div>
        </div>
      )}

      <ConfirmationModal
        type="danger"
        title="Check Out"
        message="Are you sure you want to check out?"
        onConfirm={handleCheckOutAction}
        isOpen={checkOutWarning}
        onClose={() => setCheckOutWarning(false)}
        confirmText={isCheckOutLoading ? "Checking out..." : "Check Out"}
        isLoading={isCheckOutLoading}
      />

      <ConfirmationModal
        type="danger"
        title="Logout"
        message="Are you sure you want to logout?"
        onConfirm={() => {
          logout.mutate();
          setLogoutWarning(false);
        }}
        isOpen={logoutWarning}
        onClose={() => setLogoutWarning(false)}
      />
    </>
  );
};

export default Header;
